import { KeyboardEvent, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Box, Dot, Icon, Text } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { Form as FactorialForm, Field } from 'factorial-form'
import {
  BulkDeleteGlobalTimeRecordDocument,
  BulkDeleteGlobalTimeRecordMutation,
  DeleteFlexibleTimeRecordDocument,
  DeleteFlexibleTimeRecordMutation,
  GlobalTimeRecordsDocument,
  UpdateImputedMinutesGlobalTimeRecordDocument,
  UpdateImputedMinutesGlobalTimeRecordMutation,
} from 'generated/resources/graphql'
import moment from 'moment'

import TimePicker from 'modules/core/components/Inputs/TimePicker'
import { useMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import AddCommentModal from 'modules/project_management/components/modals/AddCommentFlexibleTrackingModal'
import ModifyHoursModal from 'modules/project_management/components/modals/ModifyHoursModal'
import {
  getMinutesFromTimeFormat,
  getTimeFormatFromMinutes,
} from 'modules/project_management/lib/formatHours'
import { CustomGlobalTimeRecord } from 'modules/project_management/lib/getCustomGlobalTimeRecordsForMyProjectsTable'
import type ProjectsStore from 'modules/project_management/store/models/Projects'

interface AddEditCellProps {
  hasComment: boolean
  openClockInMinutes: number | null
  imputedMinutes: number
  isFlexible: boolean
  weekDay: number
  customGlobalTimeRecord: CustomGlobalTimeRecord
  temporaryRecords: CustomGlobalTimeRecord[]
  canRegisterFlexibleTracking: boolean
  projectsStore: ProjectsStore
}

const formAttributes = { flexibleMinutes: '' }
const formSchema = { flexibleMinutes: 'string' }

const AddEditCell = ({
  hasComment,
  openClockInMinutes,
  imputedMinutes,
  isFlexible,
  weekDay,
  customGlobalTimeRecord,
  temporaryRecords,
  canRegisterFlexibleTracking,
  projectsStore,
}: AddEditCellProps) => {
  const [hover, setHover] = useState<boolean>(false)
  const [hoverEdit, setHoverEdit] = useState<boolean>(false)
  const [hoverComment, setHoverComment] = useState<boolean>(false)
  const [isEditable, setIsEditable] = useState<boolean>(false)
  const [isAddCommentModalOpen, setIsAddCommentModalOpen] = useState<boolean>(false)
  const [isModifyHoursModalOpen, setIsModifyHoursModalOpen] = useState<boolean>(false)
  const [editing, setEditing] = useState<boolean>(false)
  const [notShowMessage, setNotShowMessage] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.projectsNotShowWarningModal,
    false
  )
  const { year, month, day } = useParams()
  const weekStart = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD')
    .startOf('week')
    .format('YYYY-MM-DD')
  const weekEnd = moment(`${year}-${month}-${day}`, 'YYYY-MM-DD').endOf('week').format('YYYY-MM-DD')

  const refetchQueries = [
    {
      query: GlobalTimeRecordsDocument,
      variables: {
        startsOn: weekStart,
        endsOn: weekEnd,
        projectWorkerIds: projectsStore.projectWorkerIds,
      },
    },
  ]

  const [deleteFlexibleTimeRecord] = useMutation(DeleteFlexibleTimeRecordDocument, {
    extractorFn: (data: DeleteFlexibleTimeRecordMutation) =>
      data.projectManagementMutations.deleteProjectmanagementFlexibleTimeRecord,
  })

  const [updateImputedMinutes] = useMutation(UpdateImputedMinutesGlobalTimeRecordDocument, {
    extractorFn: (data: UpdateImputedMinutesGlobalTimeRecordMutation) =>
      data.projectManagementMutations.updateImputedMinutesProjectmanagementGlobalTimeRecord,
  })

  const [bulkDeleteGlobalTimeRecord] = useMutation(BulkDeleteGlobalTimeRecordDocument, {
    extractorFn: (data: BulkDeleteGlobalTimeRecordMutation) =>
      data.projectManagementMutations.bulkDeleteProjectmanagementGlobalTimeRecord,
  })

  const existOpenClockIn = openClockInMinutes !== null

  const form = useMemo(() => {
    return new FactorialForm(formAttributes, formSchema)
  }, [])

  const commentButtonAvailable = (isFlexible && imputedMinutes !== 0) || hasComment
  const currentDateProject = customGlobalTimeRecord.imputedMinutesList.find(
    (_, index) => index === weekDay
  )

  const shouldNotShowModifyHoursModal =
    form.get('flexibleMinutes').value === '' ||
    isFlexible ||
    !imputedMinutes ||
    !currentDateProject?.recordId ||
    localStorage.getItem(LOCAL_STORAGE_KEYS.projectsNotShowWarningModal) === 'true'

  const changeFlexibleTimeRecord = async (value: Field) => {
    if (editing) return

    setEditing(true)
    const newImputedMinutes = getMinutesFromTimeFormat(value)
    const cellCleared = newImputedMinutes === 0

    const emptyCell =
      (currentDateProject?.imputedMinutes || 0) + (currentDateProject?.openClockInMinutes || 0) ===
      0
    const cellNotChanged =
      (currentDateProject?.imputedMinutes || 0) + (currentDateProject?.openClockInMinutes || 0) ===
      newImputedMinutes

    if ((emptyCell && cellCleared) || cellNotChanged || !canRegisterFlexibleTracking) {
      setEditing(false)
      return
    }

    setIsEditable(false)

    // Create
    if (emptyCell && currentDateProject) {
      const index = temporaryRecords.indexOf(customGlobalTimeRecord)
      if (index > -1) {
        temporaryRecords.splice(index, 1)
      }

      const response = await updateImputedMinutes({
        variables: {
          imputedMinutes: newImputedMinutes,
          date: currentDateProject.date,
          projectWorkerId: Number(customGlobalTimeRecord.projectWorkerId),
          subprojectId: customGlobalTimeRecord.subprojectId || undefined,
        },
        refetchQueries,
      })
      const handledResult = handleResult(response, () => undefined)

      if (!handledResult.success) {
        temporaryRecords.splice(index, 0, customGlobalTimeRecord)
      }

      trackAmplitudeCreateEvent()
    }

    // Delete
    if (cellCleared) {
      if (currentDateProject?.flexibleId && !currentDateProject?.recordId) {
        await deleteFlexibleTimeRecord({
          variables: {
            flexibleTimeRecordId: Number(currentDateProject?.flexibleId),
          },
          update(cache) {
            cache.evict({
              id: cache.identify({
                customId: Number(currentDateProject?.flexibleId),
                isFlexible: true,
                __typename: 'ProjectManagementGlobalTimeRecord',
              }),
            })
          },
        })
      } else {
        await bulkDeleteGlobalTimeRecord({
          variables: {
            date: `${currentDateProject?.date}`,
            projectWorkerId: Number(customGlobalTimeRecord.projectWorkerId),
            subprojectId: customGlobalTimeRecord.subprojectId || undefined,
          },
          update(cache) {
            cache.evict({
              id: cache.identify({
                customId: Number(currentDateProject?.flexibleId),
                isFlexible: true,
                __typename: 'ProjectManagementGlobalTimeRecord',
              }),
            })
            cache.evict({
              id: cache.identify({
                customId: Number(currentDateProject?.recordId),
                isFlexible: false,
                __typename: 'ProjectManagementGlobalTimeRecord',
              }),
            })
          },
        })
      }
    }
    // Update
    if (!cellCleared && !emptyCell) {
      const currentGlobalTimeRecord = customGlobalTimeRecord.imputedMinutesList[weekDay]
      if (!currentGlobalTimeRecord?.flexibleId && !currentGlobalTimeRecord?.recordId) return

      if (currentGlobalTimeRecord?.flexibleId && !currentGlobalTimeRecord?.recordId) {
        await updateImputedMinutes({
          variables: {
            imputedMinutes: newImputedMinutes,
            date: `${currentDateProject?.date}`,
            projectWorkerId: Number(customGlobalTimeRecord.projectWorkerId),
            subprojectId: customGlobalTimeRecord.subprojectId || undefined,
          },
          optimisticResponse: {
            projectManagementMutations: {
              updateImputedMinutesProjectmanagementGlobalTimeRecord: {
                errors: [],
                globalTimeRecord: {
                  customId: Number(currentGlobalTimeRecord.flexibleId),
                  __typename: 'ProjectManagementGlobalTimeRecord',
                  imputedMinutes: newImputedMinutes,
                  isFlexible: !!currentDateProject?.isFlexible,
                },
              },
            },
          },
        })
      } else {
        await updateImputedMinutes({
          variables: {
            imputedMinutes: newImputedMinutes,
            date: `${currentDateProject?.date}`,
            projectWorkerId: Number(customGlobalTimeRecord.projectWorkerId),
            subprojectId: customGlobalTimeRecord.subprojectId || undefined,
          },
          refetchQueries,
        })
      }

      trackAmplitudeUpdateEvent()
    }

    setEditing(false)
  }

  const closeEditableCellWithoutChanges = () => {
    const currentImputedMinutes =
      (currentDateProject?.imputedMinutes || 0) + (openClockInMinutes || 0)
    const newImputedMinutes = getMinutesFromTimeFormat(form.get('flexibleMinutes').value)
    if (currentImputedMinutes === newImputedMinutes) setIsEditable(false)
  }

  const onBlur = () => {
    shouldNotShowModifyHoursModal
      ? changeFlexibleTimeRecord(form.get('flexibleMinutes').value)
      : setIsModifyHoursModalOpen(true)
    closeEditableCellWithoutChanges()
  }
  const onEnterKeyDown = (e: KeyboardEvent<unknown>) => {
    if (e.key === 'Enter') {
      shouldNotShowModifyHoursModal
        ? changeFlexibleTimeRecord(form.get('flexibleMinutes').value)
        : setIsModifyHoursModalOpen(true)
      closeEditableCellWithoutChanges()
    }
  }
  const onCloseAddCommentModal = () => {
    setIsAddCommentModalOpen(false)
  }
  const onCloseModifyHoursModal = () => {
    setNotShowMessage(false)
    setIsModifyHoursModalOpen(false)
    closeEditableCellWithoutChanges()
  }
  const onSubmitModifyHoursModal = () => {
    changeFlexibleTimeRecord(form.get('flexibleMinutes').value)
    setIsModifyHoursModalOpen(false)
  }

  const trackAmplitudeCreateEvent = () => {
    trackAmplitudeEvent(
      trackingEvents.projectManagement.flexibleTracking.createHoursInFlexibleTable
    )
  }

  const trackAmplitudeUpdateEvent = () => {
    trackAmplitudeEvent(
      trackingEvents.projectManagement.flexibleTracking.updateHoursInFlexibleTable
    )
  }

  type BaseEvent = Parameters<typeof track>[0]
  const trackAmplitudeEvent = (event: BaseEvent) => {
    track(event, {
      project_id: customGlobalTimeRecord.projectId,
      subproject_id: customGlobalTimeRecord.subprojectId,
      imputed_minutes: getMinutesFromTimeFormat(form.get('flexibleMinutes').value),
    })
  }

  const isImputable =
    customGlobalTimeRecord.projectAssigned && customGlobalTimeRecord.projectStatus === 'active'

  return (
    <>
      {hover && !!isImputable ? (
        <Box
          flexDirection="row"
          justifyContent="center"
          borderRadius={{ all: 'abs008' }}
          onMouseLeave={() => setHover(false)}
        >
          <Box
            flexDirection="row"
            justifyContent="center"
            background={!commentButtonAvailable || !hoverEdit ? 'grey200' : 'grey400'}
            width={!commentButtonAvailable ? 's120' : 's80'}
            padding="s8"
            borderRadius={
              !commentButtonAvailable
                ? { all: 'abs008' }
                : { topLeft: 'abs008', bottomLeft: 'abs008' }
            }
            aria-label="edit-button"
            onClick={() => {
              setIsEditable(true)
              setHover(false)
              setHoverEdit(false)
              form.setValues({ flexibleMinutes: getTimeFormatFromMinutes(imputedMinutes, true) })
            }}
            onMouseEnter={() => setHoverEdit(true)}
            onMouseLeave={() => setHoverEdit(false)}
          >
            <Icon.Medium icon="pencil" color="grey800"></Icon.Medium>
          </Box>
          {commentButtonAvailable && (
            <Box
              flexDirection="row"
              justifyContent="center"
              background={hoverComment ? 'grey400' : 'grey200'}
              width="s40"
              padding="s8"
              borderRadius={{ topRight: 'abs008', bottomRight: 'abs008' }}
              aria-label="comment-button"
              onClick={() => {
                setIsAddCommentModalOpen(true)
                setHover(false)
                setHoverComment(false)
              }}
              onMouseEnter={() => setHoverComment(true)}
              onMouseLeave={() => setHoverComment(false)}
            >
              <Icon.Medium icon="comment" color="grey800"></Icon.Medium>
            </Box>
          )}
        </Box>
      ) : (
        <Box
          tabIndex={0}
          onFocus={() => {
            isImputable && setIsEditable(true)
            form.setValues({
              flexibleMinutes: getTimeFormatFromMinutes(
                imputedMinutes + (openClockInMinutes || 0),
                true
              ),
            })
          }}
          flexDirection="row"
          justifyContent="center"
          onMouseEnter={() => canRegisterFlexibleTracking && !isEditable && setHover(true)}
        >
          {isEditable && canRegisterFlexibleTracking ? (
            <Box maxWidth="s120" background="grey200" borderRadius={{ all: 'abs008' }}>
              <TimePicker
                field={form.get('flexibleMinutes')}
                hideBorder
                textAlign="center"
                onBlur={onBlur}
                onKeyDown={onEnterKeyDown}
                autofocus
                readonly={editing}
              />
            </Box>
          ) : (
            <Box
              style={{ cursor: canRegisterFlexibleTracking ? 'default' : 'not-allowed' }}
              flexDirection="row"
              justifyContent="center"
              alignItems="center"
            >
              {existOpenClockIn ? (
                <Box flexDirection="row" alignSelf="center" paddingRight="s12">
                  <Box alignSelf="center" paddingRight="s4">
                    <Dot type={'correct'} blinking />
                  </Box>
                  <Text.Normal>
                    {getTimeFormatFromMinutes(imputedMinutes + (openClockInMinutes || 0), false)}
                  </Text.Normal>
                </Box>
              ) : (
                <Text.Normal>{getTimeFormatFromMinutes(imputedMinutes, false)}</Text.Normal>
              )}
              {hasComment && (
                <Box paddingLeft="s4">
                  <Icon.Small icon="comment" color="grey600"></Icon.Small>
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
      {isAddCommentModalOpen && (
        <AddCommentModal
          currentDateProject={currentDateProject}
          customGlobalTimeRecord={customGlobalTimeRecord}
          refetchQueries={refetchQueries}
          onClose={onCloseAddCommentModal}
        />
      )}
      {isModifyHoursModalOpen && (
        <ModifyHoursModal
          onClose={onCloseModifyHoursModal}
          onSubmit={onSubmitModifyHoursModal}
          notShowMessage={!!notShowMessage}
          setNotShowMessage={setNotShowMessage}
        />
      )}
    </>
  )
}

export default AddEditCell
